import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FileText, Search } from 'lucide-react';
import { api } from '@/shared/api/cliente';
import { Tabla, type Columna } from '@/shared/ui/Tabla';
import { useValorDiferido } from '@/shared/ui/useValorDiferido';
import { enDinero } from '@/shared/ui/graficos';
import { BotonesDeExportacion } from './BotonesDeExportacion';

interface Factura {
  numero: string | null;
  cliente: string;
  identificacion: string;
  periodo: string;
  fechaEmision: string | null;
  total: number;
  estado: string;
  situacionSri: string;
  autorizacion: string | null;
  sistema: string;
}

interface Hoja {
  contenido: Factura[];
  totalElementos: number;
  pagina: number;
  tamanio: number;
  totalPaginas: number;
  importe: number;
}

const SITUACIONES = [
  { valor: '', etiqueta: 'Todas' },
  { valor: 'AUTORIZADA', etiqueta: 'Autorizadas' },
  { valor: 'RECHAZADA', etiqueta: 'Rechazadas' },
  { valor: 'EN_COLA', etiqueta: 'En cola' },
  { valor: 'SIN_ENVIAR', etiqueta: 'Sin enviar' },
  { valor: 'DE_BAJA', etiqueta: 'Dadas de baja' },
];

const TONO: Record<string, string> = {
  autorizada: 'text-marca-700',
  rechazada: 'text-red-700',
  interna: 'text-armazon-400',
  anulada_ante_sri: 'text-armazon-400',
};

const fecha = (iso: string | null) => (iso ? iso.split('-').reverse().join('/') : '—');

export function ListaDeFacturasSri({ desde, hasta }: { desde: string; hasta: string }) {
  const [porEmision, setPorEmision] = useState(false);
  const [situacion, setSituacion] = useState('');
  const [buscar, setBuscar] = useState('');
  const [pagina, setPagina] = useState(0);
  const [tamanio, setTamanio] = useState(25);
  const buscado = useValorDiferido(buscar.trim());

  const filtro = {
    desde,
    hasta,
    porEmision: String(porEmision),
    ...(situacion ? { situacion } : {}),
    ...(buscado ? { buscar: buscado } : {}),
  };

  const hoja = useQuery({
    queryKey: ['informe-sri-facturas', filtro, pagina, tamanio],
    queryFn: () =>
      api.get<Hoja>('/informes/facturacion/sri/facturas', {
        query: { ...filtro, pagina, tamanio },
      }),
  });

  const columnas: Columna<Factura>[] = [
    {
      clave: 'numero',
      encabezado: 'Factura',
      contenido: (f) => (
        <span className="block">
          <span className="importe block text-xs font-semibold text-armazon-800">
            {f.numero ?? 'Sin número'}
          </span>
          <span className="importe block text-[10px] text-armazon-400">
            Emitida {fecha(f.fechaEmision)} ·{' '}
            {f.sistema === 'ANTERIOR' ? 'sistema anterior' : 'SIREPAGAL'}
          </span>
        </span>
      ),
    },
    {
      clave: 'cliente',
      encabezado: 'Contribuyente',
      contenido: (f) => (
        <span className="block">
          <span className="block text-xs uppercase text-armazon-800">{f.cliente}</span>
          <span className="importe block text-[10px] text-armazon-400">{f.identificacion}</span>
        </span>
      ),
    },
    {
      clave: 'periodo',
      encabezado: 'Mes',
      contenido: (f) => <span className="importe text-xs">{f.periodo}</span>,
    },
    {
      clave: 'total',
      encabezado: 'Total',
      alinear: 'derecha',
      contenido: (f) => <span className="importe text-xs">{enDinero(f.total)}</span>,
    },
    {
      clave: 'sri',
      encabezado: 'Ante el SRI',
      contenido: (f) => (
        <span className="block" title={f.autorizacion ?? undefined}>
          <span
            className={`block text-[11px] font-semibold uppercase ${TONO[f.situacionSri] ?? 'text-amber-700'}`}
          >
            {f.situacionSri.replace(/_/g, ' ')}
          </span>
          <span className="block text-[10px] uppercase text-armazon-400">{f.estado}</span>
        </span>
      ),
    },
  ];

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-[11px] uppercase text-armazon-500">
          {hoja.data
            ? `${hoja.data.totalElementos.toLocaleString('es-EC')} facturas por ${enDinero(hoja.data.importe)}`
            : 'Contando…'}
        </p>
        <BotonesDeExportacion
          rotulo="Facturas"
          ruta="/informes/facturacion/sri/facturas/descarga"
          nombre="facturas-ante-el-sri"
          parametros={filtro}
        />
      </div>
      <Tabla
        datos={hoja.data}
        cargando={hoja.isPending}
        columnas={columnas}
        tamanio={tamanio}
        alCambiarPagina={setPagina}
        alCambiarTamanio={(t) => {
          setTamanio(t);
          setPagina(0);
        }}
        entidad="facturas"
        iconoVacio={FileText}
        mensajeVacio="No hay facturas con ese filtro"
        herramientas={
          <span className="flex flex-wrap items-center gap-2">
            <select
              value={porEmision ? 'emision' : 'periodo'}
              onChange={(e) => {
                setPorEmision(e.target.value === 'emision');
                setPagina(0);
              }}
              aria-label="El período se aplica a"
              className="h-9 rounded-lg border border-armazon-200 bg-white px-2 text-xs uppercase"
            >
              <option value="periodo">Por mes de servicio</option>
              <option value="emision">Por fecha de emisión</option>
            </select>
            <select
              value={situacion}
              onChange={(e) => {
                setSituacion(e.target.value);
                setPagina(0);
              }}
              aria-label="Ante el SRI"
              className="h-9 rounded-lg border border-armazon-200 bg-white px-2 text-xs uppercase"
            >
              {SITUACIONES.map((s) => (
                <option key={s.valor} value={s.valor}>
                  {s.etiqueta}
                </option>
              ))}
            </select>
            <label className="relative">
              <Search
                className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-armazon-400"
                aria-hidden
              />
              <input
                value={buscar}
                onChange={(e) => {
                  setBuscar(e.target.value);
                  setPagina(0);
                }}
                placeholder="Cédula, nombre o número…"
                className="h-9 w-56 rounded-lg border border-armazon-200 pl-8 pr-2 text-xs"
              />
            </label>
          </span>
        }
      />
    </div>
  );
}
